import {GASubpageInfo} from '../types/analyticsTypes';
import {RootState, store} from '../redux/store';

export const GA_EVENT_NAMES = {
  PAGE_VIEW: 'page-view',
  CLICK: 'click',
  SUB_PAGE_VIEW: 'sub-page-view',
  ERROR: 'error',
  ADD: 'add',
  APPLY: 'apply',
  CANCEL: 'cancel',
  CHANGE: 'change',
  CONFIRMATION: 'confirmation',
  COPY: 'copy',
  DOWNLOAD: 'download',
  FILTER: 'filter',
  NAV: 'nav',
  OTHER: 'other',
  RAN: 'ran',
  SAVE: 'save',
  SEARCH: 'search',
  SELECT: 'select',
  EXPORT: 'export',
  FILE_DOWNLOAD: 'file_download',
  PRINT: 'print',
  DELETE: 'delete',
};

export const GA_INTERNAL_DOMAINS = [
  'consilio.com',
  'consiliotest.com',
  'consilioservices.com',
];

export const GA_REPORT_EVENT_DETAILS = {
  REPORT_VIEW: 'report-view',
  REPORT_SUBPAGE_VIEW: 'report-subpage-view',
  REPORT_PAGINATION: 'report-pagination',
  REPORT_INTERACTION: 'report-interaction',
  REPORT_EXPORT: 'report-export',

  CHANGE_PROJECT_SUBPAGE: 'change-project-subpage',
  CHANGE_PROJECT_WORKSPACE: 'change-project-workspace',

  CUSTOM_REPORT_LIST_VIEW: 'custom-report-list-view',
  CUSTOM_REPORT_SUBPAGE_VIEW: 'custom-report-subpage-view',

  DOWNLOAD: 'download',
  PRINT: 'print',
};

export const GA_REPORT_TYPE = {
  CUSTOM: 'custom',
  STANDARD: 'standard',
};

export const GA_PREFIX = 'aurora';

export const GA_CLICK_TYPES = {
  ICON: 'icon',
  BUTTON: 'button',
  LINK: 'link',
  IMG: 'image',
  DROPDOWN: 'dropdown',
  TILE: 'tile',
  LIST_ITEM: 'list-item',
  INPUT: 'input',
  ADVANCE: 'advance',
  CHECKBOX: 'checkbox',
  TAB: 'tab',
  DRAG_DROP: 'drag and drop',
  EVENT: 'event',
  TOGGLE: 'toggle',
  LIST: 'list',
  RADIO_BUTTON: 'radio button',
  ACCORDION: 'accordion',
  DRAG: 'drag',
};

export const GA_REGIONS = {
  LEFT_RAIL: 'left rail',
  RIGHT_RAIL: 'right rail',
  TOP_RAIL: 'top rail',
  FOOTER: 'footer',
  REPORT_TOOL_BAR: 'report tool bar',
  BODY: 'body',
  MODAL: 'modal',
  WIDGET_TOP_NAV: 'widget top nav',
  WIDGET_LEFT_NAV: 'widget left nav',
  WIDGET_BODY: 'widget body',
  TYPED_OR_BOOKMARKED: 'typed/bookmarked',
  LIST_ITEM: 'list item',
  DRAWER: 'drawer',
  DROPDOWN: 'dropdown',
  TAB: 'tab',
  HEADER: 'header',
  TOP_NAV: 'top nav',
  LEFT_NAV_TAB: 'left nav tab',
  LEFT_NAV_BAR: 'left nav bar',
};

export const GA_ERROR_CODES = {
  NOT_FOUND_404: '404',
  NOT_AUTHORIZED_401: '401',
  BAD_REQUEST: '400',
  INTERNAL_SERVER_ERR_500: '500',
};

export const GA_ERROR_TYPES = {
  PAGE_NOT_FOUND: 'page not found',
  VALIDATION_ERROR: 'validation error',
  AUTHENTICATION_ERROR: 'authentication error',
  FILE_UPLOAD_ERROR: 'file upload error',
};

export const GA_SUBPAGE_TYPE = {
  MODAL: 'modal',
  TAB: 'tab',
  BOX_LOAD: 'box-load',
  IN_BODY: 'in-body',
  DRAWER: 'drawer',
  ACCORDION: 'accordion',
};

export const GA_SOURCE = {
  BODY: 'body',
  LEFT_RAIL: 'left rail',
  REPORT_TOOL_BAR: 'report tool bar',
};

export const GA_FILE_EXTENTIONS = {
  XLSX: 'xlsx',
  CSV: 'csv',
  PDF: 'pdf',
};

export const GA_BLACKLIST_URLS = [
  '/reports',
  '/callback',
  '/',
  '/customreports',
  '/str/view',
  '/users/bulkupload/results',
  '/indexmanagement/settings',
];

export const GA_MEMBER_URLS = ['/users/details'];

export const GA_NON_USER_ROUTES = ['/login', '/callback', '/logout', '/unavailable'];

export const GA_DATA_MODE_AUTO = 'AUTO' as const;

export const GA_STATIC_URL_MAPPING: Record<string, string> = {
  '/callback': '/login',
};

export const GA_PAGE_WITH_SUBPAGE_DATA: Record<
  string,
  GASubpageInfo | ((state: RootState) => GASubpageInfo)
> = {
  '/users/add': {
    subpage_name: 'team member info',
    subpage_type: GA_SUBPAGE_TYPE.TAB,
  },
  '/users/bulkupload/add': {
    subpage_name: 'select a client',
    subpage_type: GA_SUBPAGE_TYPE.MODAL,
  },
  '/str?Flow=Create': {
    subpage_name: 'Basic Info',
    subpage_type: GA_SUBPAGE_TYPE.TAB,
  },
  '/eci-home-page/create-new-export': {
    subpage_name: 'general info',
    subpage_type: GA_SUBPAGE_TYPE.TAB,
  },
  '/indexmanagement/create-custom-index': {
    subpage_name: 'general info',
    subpage_type: GA_SUBPAGE_TYPE.TAB,
  },
  '/upload-evidence/new-evidence': {
    subpage_name: 'source details',
    subpage_type: GA_SUBPAGE_TYPE.TAB,
  },
  '/upload-file-catalyst': {
    subpage_name: 'drag-and-drop upload',
    subpage_type: GA_SUBPAGE_TYPE.TAB,
  },
  '/welcome-multi-client': (state: RootState) => ({
    subpage_name: state.pageViewState.welcomeMultiClientTab
      ? 'project list view'
      : 'client list view',
    subpage_type: GA_SUBPAGE_TYPE.TAB,
  }),
};

export const getSubpageData = (path: string): GASubpageInfo | undefined => {
  const config = GA_PAGE_WITH_SUBPAGE_DATA[path];
  if (typeof config === 'function') {
    return config(store.getState());
  }
  return config;
};
